import type { LocalizedString } from "@repo/shared/types";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { CsvDataParser, type CsvDataPoint, dataPointsToCsv } from "@/components/ui/csv-parser";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LocalizedInput } from "@/components/ui/localized-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ApiError } from "@/lib/api/_client";
import {
  useCreateCustomInstrument,
  useCustomInstrument,
  useUpdateCustomInstrument,
} from "@/lib/hooks/use-custom-instruments";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./manage-custom-instrument-dialog.i18n";

const CURRENCIES = ["NOK", "USD", "EUR", "GBP", "SEK", "DKK"];

type ManageCustomInstumentDialogProps = {
  open: boolean;
  editingTicker: string | null;
  onClose: () => void;
};

export function ManageCustomInstumentDialog({
  open,
  editingTicker,
  onClose,
}: ManageCustomInstumentDialogProps) {
  const { userLanguage, tenantSupportedLanguages } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);
  const { instrument, isLoading: isLoadingInstrument } = useCustomInstrument(
    open ? editingTicker : null,
  );
  const isEditing = !!editingTicker;

  const [name, setName] = useState("");
  const [nameI18n, setNameI18n] = useState<LocalizedString>({});
  const [formData, setFormData] = useState({
    ticker: "",
    currency: "NOK",
  });
  const [csvInitialValue, setCsvInitialValue] = useState("");
  const [parsedTimeSeries, setParsedTimeSeries] = useState<CsvDataPoint[] | null>(null);
  const createMutation = useCreateCustomInstrument();
  const updateMutation = useUpdateCustomInstrument();
  const isPending = createMutation.isPending || updateMutation.isPending;
  const [error, setError] = useState<string | null>(null);

  // Populate form when instrument data loads (edit mode)
  useEffect(() => {
    if (open && instrument) {
      const csvText = dataPointsToCsv(instrument.data);
      const instrumentName = instrument.nameI18n
        ? localize(instrument.nameI18n as LocalizedString)
        : instrument.name;
      setName(instrumentName);
      setNameI18n((instrument.nameI18n as LocalizedString) || {});
      setFormData({
        ticker: instrument.ticker,
        currency: instrument.currency,
      });
      setCsvInitialValue(csvText);
      setParsedTimeSeries(instrument.data);
    }
  }, [open, instrument, localize]);

  // Reset form when opening in create mode
  useEffect(() => {
    if (open && !editingTicker) {
      setName("");
      setNameI18n({});
      setFormData({ ticker: "", currency: "NOK" });
      setCsvInitialValue("");
      setParsedTimeSeries(null);
      setError(null);
    }
  }, [open, editingTicker]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!parsedTimeSeries || parsedTimeSeries.length === 0) {
      return;
    }

    try {
      const finalNameI18n: LocalizedString = {
        ...nameI18n,
        [userLanguage]: name,
      };

      const payload = {
        name,
        nameI18n: finalNameI18n,
        ticker: formData.ticker,
        currency: formData.currency,
        data: parsedTimeSeries,
      };

      if (isEditing) {
        await updateMutation.mutateAsync({ ticker: formData.ticker, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }

      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.failedToSave);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditing ? t.editCustomInstument : t.addCustomInstument}</DialogTitle>
          <DialogDescription>
            {isEditing ? t.editCustomInstumentDescription : t.addCustomInstumentDescription}
          </DialogDescription>
        </DialogHeader>

        {isEditing && isLoadingInstrument ? (
          <div className="py-8 text-center text-muted-foreground">{t.loading}</div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {error ? (
                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                  {error}
                </div>
              ) : null}

              <LocalizedInput
                id="name"
                label={t.name}
                value={name}
                i18nValue={nameI18n}
                onChange={setName}
                onI18nChange={setNameI18n}
                supportedLanguages={tenantSupportedLanguages}
                placeholder={t.namePlaceholder}
                required
              />

              <div className="space-y-2">
                <Label htmlFor="currency">
                  {t.currency} <span className="text-destructive">*</span>
                </Label>
                <Select
                  disabled={isEditing}
                  value={formData.currency}
                  onValueChange={(value) => setFormData({ ...formData, currency: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t.selectCurrency} />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((cur) => (
                      <SelectItem key={cur} value={cur}>
                        {cur}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ticker">
                  {t.ticker} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="ticker"
                  disabled={isEditing}
                  value={formData.ticker}
                  onChange={(e) => setFormData({ ...formData, ticker: e.target.value })}
                  placeholder={t.tickerPlaceholder}
                  required
                />
              </div>

              <CsvDataParser
                id="timeSeries"
                label={t.timeSeries}
                initialValue={csvInitialValue}
                onParsedData={setParsedTimeSeries}
                placeholder={t.timeSeriesPlaceholder}
                textErrorMessage={t.invalidCsvStructure}
                fileErrorMessage={t.invalidCsvStructure}
                textHelpText={t.textCsvStructure}
                fileHelpText={t.fileCsvStructure}
                required
              />
            </div>

            <DialogFooter>
              <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
              <Button type="submit" loading={isPending}>
                {isEditing ? t.save : t.addCustomInstrument}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
